import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, Check, RotateCcw, SlidersHorizontal, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { tx } from "../lib/tx";

const FILTERS_STORAGE_KEY = "discover_advanced_filters";

// Complete filter configuration based on user requirements
const FILTER_CONFIG = {
  identity: {
    title: "Identité & bases",
    icon: "👤",
    fields: {
      gender: {
        label: "Genre recherché",
        type: "single",
        options: [
          { value: "Femme", label: "Femme" },
          { value: "Homme", label: "Homme" },
          { value: "non-binary", label: "Non-binaire" },
          { value: "", label: "Tous" }
        ]
      },
      orientation: {
        label: "Orientation",
        type: "single",
        options: [
          { value: "hetero", label: "Hétérosexuel(le)" },
          { value: "homo", label: "Homosexuel(le)" },
          { value: "bi", label: "Bisexuel(le)" },
          { value: "pan", label: "Pansexuel(le)" },
          { value: "", label: "Tous" }
        ]
      },
      zone: {
        label: "Ville / Zone",
        type: "single",
        options: [
          { value: "center", label: "Centre-ville" },
          { value: "suburb", label: "Périphérie" },
          { value: "rural", label: "Campagne proche" },
          { value: "", label: "Toutes" }
        ]
      }
    }
  },
  physical: {
    title: "Physique",
    icon: "📏",
    fields: {
      height: {
        label: "Taille",
        type: "single",
        options: [
          { value: "<150", label: "< 150 cm" },
          { value: "150-159", label: "150-159 cm" },
          { value: "160-169", label: "160-169 cm" },
          { value: "170-179", label: "170-179 cm" },
          { value: "180-189", label: "180-189 cm" },
          { value: "190+", label: "190+ cm" },
          { value: "", label: "Peu importe" }
        ]
      },
      body_type: {
        label: "Silhouette",
        type: "single",
        options: [
          { value: "slim", label: "Mince" },
          { value: "athletic", label: "Athlétique" },
          { value: "average", label: "Normal(e)" },
          { value: "curvy", label: "Ronde" },
          { value: "voluptuous", label: "Pulpeuse" },
          { value: "full", label: "Corpulent(e)" },
          { value: "", label: "Peu importe" }
        ]
      },
      eyes: {
        label: "Couleur des yeux",
        type: "single",
        options: [
          { value: "brown", label: "Marron" },
          { value: "blue", label: "Bleu" },
          { value: "green", label: "Vert" },
          { value: "hazel", label: "Noisette" },
          { value: "other", label: "Autre" },
          { value: "", label: "Peu importe" }
        ]
      },
      hair: {
        label: "Couleur des cheveux",
        type: "single",
        options: [
          { value: "brown", label: "Bruns" },
          { value: "blonde", label: "Blonds" },
          { value: "chestnut", label: "Châtains" },
          { value: "red", label: "Roux" },
          { value: "black", label: "Noirs" },
          { value: "gray", label: "Gris / blancs" },
          { value: "bald", label: "Chauve" },
          { value: "", label: "Peu importe" }
        ]
      },
      beard: {
        label: "Barbe (hommes)",
        type: "single",
        options: [
          { value: "yes", label: "Oui" },
          { value: "no", label: "Non" },
          { value: "sometimes", label: "Parfois" },
          { value: "", label: "Peu importe" }
        ]
      }
    }
  },
  lifestyle: {
    title: "Habitudes & lifestyle",
    icon: "🚬",
    fields: {
      smoking: {
        label: "Fumeur",
        type: "single",
        options: [
          { value: "no", label: "Non" },
          { value: "occasional", label: "Occasionnel" },
          { value: "yes", label: "Régulier" },
          { value: "", label: "Peu importe" }
        ]
      },
      drinking: {
        label: "Alcool",
        type: "single",
        options: [
          { value: "never", label: "Jamais" },
          { value: "occasional", label: "Occasionnel" },
          { value: "regular", label: "Régulier" },
          { value: "", label: "Peu importe" }
        ]
      },
      sport: {
        label: "Sport",
        type: "single",
        options: [
          { value: "never", label: "Jamais" },
          { value: "occasional", label: "Occasionnel" },
          { value: "regular", label: "Régulier" },
          { value: "intensive", label: "Intensif" },
          { value: "", label: "Peu importe" }
        ]
      },
      diet: {
        label: "Alimentation",
        type: "single",
        options: [
          { value: "classic", label: "Classique" },
          { value: "vegetarian", label: "Végétarien(ne)" },
          { value: "vegan", label: "Végan" },
          { value: "flexitarian", label: "Flexitarien(ne)" },
          { value: "other", label: "Autre" },
          { value: "", label: "Peu importe" }
        ]
      },
      pets: {
        label: "Animaux",
        type: "single",
        options: [
          { value: "none", label: "Aucun" },
          { value: "dog", label: "Chien" },
          { value: "cat", label: "Chat" },
          { value: "other", label: "Autres" },
          { value: "loves", label: "Aime les animaux" },
          { value: "", label: "Peu importe" }
        ]
      }
    }
  },
  family: {
    title: "Enfants & famille",
    icon: "👶",
    fields: {
      children: {
        label: "Enfants",
        type: "single",
        options: [
          { value: "none", label: "Aucun" },
          { value: "has", label: "Avec moi" },
          { value: "shared", label: "En garde partagée" },
          { value: "", label: "Peu importe" }
        ]
      },
      wants_children: {
        label: "Souhaite des enfants",
        type: "single",
        options: [
          { value: "yes", label: "Oui" },
          { value: "no", label: "Non" },
          { value: "maybe", label: "Peut-être" },
          { value: "", label: "Peu importe" }
        ]
      },
      family_status: {
        label: "Situation familiale",
        type: "single",
        options: [
          { value: "single", label: "Célibataire" },
          { value: "divorced", label: "Divorcé(e)" },
          { value: "separated", label: "Séparé(e)" },
          { value: "widowed", label: "Veuf / veuve" },
          { value: "", label: "Peu importe" }
        ]
      }
    }
  },
  education: {
    title: "Études & travail",
    icon: "🎓",
    fields: {
      education: {
        label: "Niveau d'études",
        type: "single",
        options: [
          { value: "none", label: "Aucun diplôme" },
          { value: "cap", label: "CAP / BEP" },
          { value: "bac", label: "Bac" },
          { value: "bac2", label: "Bac +2" },
          { value: "bac3", label: "Bac +3" },
          { value: "bac5", label: "Bac +5" },
          { value: "phd", label: "Doctorat" },
          { value: "", label: "Peu importe" }
        ]
      },
      profession: {
        label: "Profession",
        type: "single",
        options: [
          { value: "student", label: "Étudiant(e)" },
          { value: "employee", label: "Employé(e)" },
          { value: "manager", label: "Cadre" },
          { value: "freelance", label: "Indépendant(e)" },
          { value: "entrepreneur", label: "Entrepreneur(e)" },
          { value: "unemployed", label: "Sans emploi" },
          { value: "retired", label: "Retraité(e)" },
          { value: "", label: "Peu importe" }
        ]
      },
      sector: {
        label: "Secteur",
        type: "single",
        options: [
          { value: "health", label: "Santé" },
          { value: "tech", label: "Informatique" },
          { value: "commerce", label: "Commerce" },
          { value: "education", label: "Éducation" },
          { value: "craft", label: "Artisanat" },
          { value: "art", label: "Artistique" },
          { value: "other", label: "Autre" },
          { value: "", label: "Peu importe" }
        ]
      }
    }
  },
  intention: {
    title: "Intention de rencontre",
    icon: "💖",
    fields: {
      looking_for: {
        label: "Type de relation recherchée",
        type: "single",
        options: [
          { value: "serious", label: "Sérieuse" },
          { value: "friendship", label: "Amicale" },
          { value: "casual", label: "Occasionnelle" },
          { value: "no_pressure", label: "Sans pression" },
          { value: "undecided", label: "Je ne sais pas encore" },
          { value: "", label: "Peu importe" }
        ]
      },
      pace: {
        label: "Rythme",
        type: "single",
        options: [
          { value: "slow", label: "Lent" },
          { value: "normal", label: "Normal" },
          { value: "fast", label: "Rapide" },
          { value: "", label: "Peu importe" }
        ]
      },
      exclusivity: {
        label: "Exclusivité",
        type: "single",
        options: [
          { value: "yes", label: "Oui" },
          { value: "no", label: "Non" },
          { value: "maybe", label: "À voir" },
          { value: "", label: "Peu importe" }
        ]
      }
    }
  },
  personality: {
    title: "Loisirs & personnalité",
    icon: "🎶",
    fields: {
      interests: {
        label: "Centres d'intérêt",
        type: "multi",
        options: [
          { value: "travel", label: "Voyage" },
          { value: "music", label: "Musique" },
          { value: "cinema", label: "Cinéma / séries" },
          { value: "reading", label: "Lecture" },
          { value: "sport", label: "Sport" },
          { value: "cooking", label: "Cuisine" },
          { value: "going_out", label: "Sorties" },
          { value: "gaming", label: "Jeux vidéo" },
          { value: "nature", label: "Nature" }
        ]
      },
      personality: {
        label: "Personnalité",
        type: "multi",
        options: [
          { value: "calm", label: "Calme" },
          { value: "dynamic", label: "Dynamique" },
          { value: "funny", label: "Drôle" },
          { value: "introvert", label: "Introverti(e)" },
          { value: "extrovert", label: "Extraverti(e)" },
          { value: "sensitive", label: "Sensible" },
          { value: "ambitious", label: "Ambitieux(se)" }
        ]
      },
      humor: {
        label: "Humour",
        type: "single",
        options: [
          { value: "very", label: "Très important" },
          { value: "important", label: "Important" },
          { value: "not", label: "Peu important" },
          { value: "", label: "Peu importe" }
        ]
      },
      open_minded: {
        label: "Ouverture d'esprit",
        type: "single",
        options: [
          { value: "low", label: "Faible" },
          { value: "medium", label: "Moyenne" },
          { value: "high", label: "Élevée" },
          { value: "", label: "Peu importe" }
        ]
      }
    }
  },
  values: {
    title: "Valeurs & limites",
    icon: "🔞",
    fields: {
      religion: {
        label: "Religion",
        type: "single",
        options: [
          { value: "none", label: "Aucune" },
          { value: "christian", label: "Chrétienne" },
          { value: "muslim", label: "Musulmane" },
          { value: "jewish", label: "Juive" },
          { value: "buddhist", label: "Bouddhiste" },
          { value: "other", label: "Autre" },
          { value: "", label: "Peu importe" }
        ]
      },
      politics: {
        label: "Politique",
        type: "single",
        options: [
          { value: "none", label: "Ne se prononce pas" },
          { value: "left", label: "Gauche" },
          { value: "center", label: "Centre" },
          { value: "right", label: "Droite" },
          { value: "", label: "Peu importe" }
        ]
      },
      fidelity: {
        label: "Fidélité",
        type: "single",
        options: [
          { value: "essential", label: "Essentielle" },
          { value: "important", label: "Importante" },
          { value: "not_priority", label: "Pas prioritaire" },
          { value: "", label: "Peu importe" }
        ]
      }
    }
  }
};

export default function AdvancedFiltersModal({ isOpen, onClose, onApply, baseFilters }) {
  const [filters, setFilters] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [ageRange, setAgeRange] = useState([18, 65]);
  const [maxDistance, setMaxDistance] = useState(100);

  useEffect(() => {
    if (isOpen) {
      // Load saved advanced filters
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setFilters(parsed.advanced || {});
        setAgeRange(parsed.ageRange || [18, 65]);
        setMaxDistance(parsed.maxDistance || 100);
      }
      // Also apply base filters if available
      if (baseFilters) {
        setAgeRange([baseFilters.min_age || 18, baseFilters.max_age || 65]);
        setMaxDistance(baseFilters.max_distance || 100);
        if (baseFilters.gender) {
          setFilters(prev => ({ ...prev, gender: baseFilters.gender }));
        }
      }
    }
  }, [isOpen, baseFilters]);

  const handleSelect = (field, value, isMulti = false) => {
    setFilters(prev => {
      if (isMulti) {
        const current = prev[field] || [];
        const newValue = current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value];
        return { ...prev, [field]: newValue };
      }
      // For single select, toggle off if same value
      return { ...prev, [field]: prev[field] === value ? "" : value };
    });
  };

  const handleApply = () => {
    const allFilters = {
      advanced: filters,
      ageRange,
      maxDistance,
      // Convert to base filter format
      min_age: ageRange[0],
      max_age: ageRange[1],
      max_distance: maxDistance,
      gender: filters.gender || ""
    };
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(allFilters));
    onApply(allFilters);
  };

  const handleReset = () => {
    setFilters({});
    setAgeRange([18, 65]);
    setMaxDistance(100);
    localStorage.removeItem(FILTERS_STORAGE_KEY);
  };

  const countActiveFilters = () => {
    let count = 0;
    Object.values(filters).forEach(v => {
      if (v && (Array.isArray(v) ? v.length > 0 : v !== "")) count++;
    });
    if (ageRange[0] !== 18 || ageRange[1] !== 65) count++;
    if (maxDistance !== 100) count++;
    return count;
  };

  if (!isOpen) return null;

  // Field selection view
  if (activeField) {
    const category = Object.values(FILTER_CONFIG).find(c => 
      Object.keys(c.fields).includes(activeField)
    );
    const field = category?.fields[activeField];
    const isMulti = field?.type === "multi";

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-background"
      >
        <header className="h-16 flex items-center justify-between px-4 border-b border-white/10 bg-card">
          <button 
            onClick={() => setActiveField(null)}
            className="flex items-center gap-2 text-white hover:text-primary transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>{tx("Retour")}</span>
          </button>
          <span className="font-semibold text-white">{tx(field?.label)}</span>
          <div className="w-20" />
        </header>
        
        <div className="p-4 space-y-2 max-h-[calc(100vh-8rem)] overflow-y-auto pb-24">
          {isMulti && (
            <p className="text-sm text-muted-foreground mb-4 text-center">
              {tx("Sélectionnez plusieurs options")}
            </p>
          )}
          {field?.options.map(opt => {
            const isSelected = isMulti 
              ? (filters[activeField] || []).includes(opt.value)
              : filters[activeField] === opt.value;
            
            return (
              <button
                key={opt.value}
                onClick={() => handleSelect(activeField, opt.value, isMulti)}
                className={`w-full p-4 rounded-xl flex items-center justify-between transition-all ${
                  isSelected 
                    ? 'bg-primary/20 border-2 border-primary' 
                    : 'bg-muted/30 border-2 border-transparent hover:border-white/20'
                }`}
              >
                <span className="text-white">{tx(opt.label)}</span>
                {isSelected && <Check className="w-5 h-5 text-primary" />}
              </button>
            );
          })}
        </div>
        
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-white/10">
          <Button onClick={() => setActiveField(null)} className="w-full h-12 bg-primary rounded-xl">
            {tx("Confirmer")}
          </Button>
        </div>
      </motion.div>
    );
  }

  // Category view
  if (activeCategory) {
    const category = FILTER_CONFIG[activeCategory];
    
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-background"
      >
        <header className="h-16 flex items-center justify-between px-4 border-b border-white/10 bg-card">
          <button 
            onClick={() => setActiveCategory(null)}
            className="flex items-center gap-2 text-white hover:text-primary transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span>{tx("Retour")}</span>
          </button>
          <span className="font-semibold text-white">
            {category.icon} {tx(category.title)}
          </span>
          <div className="w-20" />
        </header>
        
        <div className="p-4 space-y-3 max-h-[calc(100vh-5rem)] overflow-y-auto pb-8">
          {Object.entries(category.fields).map(([key, field]) => {
            const value = filters[key];
            const hasValue = value && (Array.isArray(value) ? value.length > 0 : value !== "");
            
            return (
              <button
                key={key}
                onClick={() => setActiveField(key)}
                className="w-full p-4 rounded-xl bg-muted/30 flex items-center justify-between hover:bg-muted/50 transition-all"
              >
                <div className="text-left">
                  <span className="text-white font-medium">{tx(field.label)}</span>
                  {hasValue && (
                    <p className="text-sm text-primary mt-1">
                      {Array.isArray(value) 
                        ? `${value.length} ${tx("sélectionné(s)")}` 
                        : tx(field.options.find(o => o.value === value)?.label)}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      </motion.div>
    );
  }

  // Main categories view
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100]"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
          
          {/* Modal */}
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="absolute inset-0 bg-background rounded-t-3xl mt-12 overflow-hidden"
          >
            {/* Header */}
            <header className="h-16 flex items-center justify-between px-4 border-b border-white/10 bg-card">
              <button onClick={onClose} className="p-2 text-white hover:text-primary">
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <span className="font-semibold text-white">{tx("Filtres avancés")}</span>
              </div>
              <button onClick={handleReset} className="text-primary text-sm flex items-center gap-1">
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            </header>
            
            <div className="p-4 space-y-4 max-h-[calc(100vh-12rem)] overflow-y-auto pb-8">
              {/* Age Range */}
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-medium">{tx("Tranche d'âge")}</span>
                  <span className="text-primary font-semibold">{ageRange[0]} - {ageRange[1]} ans</span>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-xs text-muted-foreground">Minimum: {ageRange[0]} ans</span>
                    <Slider
                      value={[ageRange[0]]}
                      onValueChange={([v]) => setAgeRange([Math.min(v, ageRange[1] - 1), ageRange[1]])}
                      min={18}
                      max={99}
                      step={1}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Maximum: {ageRange[1]} ans</span>
                    <Slider
                      value={[ageRange[1]]}
                      onValueChange={([v]) => setAgeRange([ageRange[0], Math.max(v, ageRange[0] + 1)])}
                      min={18}
                      max={99}
                      step={1}
                      className="mt-2"
                    />
                  </div>
                </div>
              </div>

              {/* Distance */}
              <div className="bg-muted/30 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-medium">{tx("Distance maximum")}</span>
                  <span className="text-primary font-semibold">{maxDistance} km</span>
                </div>
                <Slider
                  value={[maxDistance]}
                  onValueChange={([v]) => setMaxDistance(v)}
                  min={5}
                  max={500}
                  step={5}
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>5 km</span>
                  <span>500 km</span>
                </div>
              </div>

              {/* Category Buttons */}
              <div className="space-y-2">
                {Object.entries(FILTER_CONFIG).map(([key, category]) => {
                  const activeCount = Object.keys(category.fields).filter(f => {
                    const v = filters[f];
                    return v && (Array.isArray(v) ? v.length > 0 : v !== "");
                  }).length;
                  
                  return (
                    <button
                      key={key}
                      onClick={() => setActiveCategory(key)}
                      className="w-full p-4 rounded-xl bg-muted/30 flex items-center justify-between hover:bg-muted/50 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{category.icon}</span>
                        <span className="text-white font-medium">{tx(category.title)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {activeCount > 0 && (
                          <span className="bg-primary text-white text-xs px-2.5 py-1 rounded-full font-semibold">
                            {activeCount}
                          </span>
                        )}
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Apply Button */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-white/10">
              <Button 
                onClick={handleApply} 
                className="w-full h-14 bg-gradient-to-r from-primary to-pink-500 rounded-xl text-lg font-bold"
              >
                <SlidersHorizontal className="w-5 h-5 mr-2" />
                {tx("Appliquer")} {countActiveFilters() > 0 && `(${countActiveFilters()})`}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
